import { useState, useEffect } from 'react';
import useSWR from 'swr';
import toast from 'react-hot-toast';
import { useAuthStore } from '../hooks/useAuth';
import { useUIStore } from '../hooks/useUIStore';
import {
    ShieldAlert,
    TrendingUp,
    AlertTriangle,
    CheckCircle,
    Mail,
    ArrowRight,
    Loader2,
    DollarSign,
    Info,
    ListChecks,
    Sparkles
} from 'lucide-react';
import { AppointmentDetailDialog } from '../components/appointments/AppointmentDetailDialog';
import { ReviewQueueDrawer } from '../components/ai/ReviewQueueDrawer';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { KPICard } from '../components/dashboard/KPICard';
import { useLocation } from 'wouter';
import { isOperationsTierUser } from '../utils/entitlements';
import { OperationsFeatureGate } from '../components/entitlements/OperationsFeatureGate';

interface AIInsightsData {
    atRiskThisWeek: {
        high: number;
        medium: number;
        low: number;
        exposureValue: number;
        topRisky: Array<{
            id: string;
            carrierName: string;
            startAt: string;
            riskLevel: string;
            riskScore: number;
            reasons: string[];
            poNumber: string;
            status: string;
        }>;
    };
    carrierLeaderboard: Array<{
        name: string;
        total: number;
        lateRate: number;
        lateCount: number;
    }>;
    outcomes: {
        missingCount: number;
    };
    outreach: {
        sentLast30d: number;
        recommended: number;
        awaitingReview: number;
    };
    headlineMetric: {
        label: string;
        value: number;
        unit: string;
    };
    // NEW FIELDS
    aiBrief: {
        title: string;
        summary: string;
        bullets: string[];
        confidence: { label: string; detail?: string };
    };
    nextActions: Array<{
        id: string;
        label: string;
        description: string;
        cta: string;
        href: string;
        count?: number;
    }>;
    anomalies: Array<{
        type: 'spike' | 'coverage' | 'process_gap';
        severity: 'low' | 'medium' | 'high';
        title: string;
        detail: string;
    }>;
}


export default function AIInsights() {
    const { token, user } = useAuthStore();
    const { openAppointmentDetail } = useUIStore();
    const [, setLocation] = useLocation();
    const [selectedWarehouseId, setSelectedWarehouseId] = useState<string>('');
    const [riskFilter, setRiskFilter] = useState<'all' | 'high' | 'medium' | 'low'>('all');
    const [isQueueOpen, setIsQueueOpen] = useState(false);
    const isAdmin = user?.role === 'admin' && user?.isAdmin === true;
    const isOperationsEntitled = isOperationsTierUser(user);
    // --- Backend Persistence for Acks ---

    // 1. Fetch active acks from backend
    // Only fetch if entitled and we have a valid target (manager always has one, admin needs selection)
    const shouldFetchAcks = isOperationsEntitled && token && (!isAdmin || selectedWarehouseId);
    const acksQuery = isAdmin && selectedWarehouseId ? `?warehouseId=${selectedWarehouseId}` : '';

    const { data: acksData, mutate: mutateAcks } = useSWR<{ ok: true, acks: Array<{ appointmentId: string, expiresAt: string }> }>(
        shouldFetchAcks ? `/api/ai/risk-acks${acksQuery}` : null,
        (url: string) => fetch(url, { headers: { 'Authorization': `Bearer ${token}` } }).then(async res => {
            if (!res.ok) {
                const text = await res.text();
                throw new Error(text || 'Failed to fetch risk acknowledgements');
            }
            return res.json();
        })
    );

    // 2. Identify acknowleged IDs (map for O(1) lookup)
    const acknowledgedItems = (acksData?.acks || []).reduce((acc, item) => {
        // Only include if not expired (server filters too, but double check for real-time drift)
        if (new Date(item.expiresAt).getTime() > Date.now()) {
            acc[item.appointmentId] = new Date(item.expiresAt).getTime();
        }
        return acc;
    }, {} as Record<string, number>);


    // 3. Handle Acknowledge (POST to backend)
    const handleAcknowledge = async (id: string, snooze = false) => {
        if (!token) return;
        const toastId = toast.loading(snooze ? 'Snoozing...' : 'Acknowledging...');

        try {
            const res = await fetch('/api/ai/risk-acks', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    appointmentId: id,
                    action: snooze ? 'snooze' : 'ack',
                    hours: 24, // simplified default
                    warehouseId: isAdmin ? selectedWarehouseId : undefined
                })
            });

            if (!res.ok) throw new Error('Failed to save');

            toast.success(snooze ? 'Snoozed for 24h' : 'Acknowledged', { id: toastId });

            // Optimistic update or re-fetch
            mutateAcks();

        } catch (e) {
            console.error(e);
            toast.error('Failed to save acknowledgement', { id: toastId });
        }
    };

    // Redirect if not manager/admin
    if (!user || (user.role !== 'warehouse_manager' && user.role !== 'admin')) {
        return (
            <div className="container mx-auto px-4 py-8">
                <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-6 text-center">
                    <p className="text-destructive font-semibold">Access Denied</p>
                    <p className="text-muted-foreground text-sm mt-1">This page is only available to warehouse managers and admins.</p>
                </div>
            </div>
        );
    }

    // Admin handling: Default to user's warehouse if set, otherwise wait for selection


    // Initialize warehouse ID for admin if they have one
    useEffect(() => {
        if (isAdmin && !selectedWarehouseId && user?.warehouseId) {
            setSelectedWarehouseId(user.warehouseId);
        }
    }, [isAdmin, selectedWarehouseId, user?.warehouseId]);

    const warehouseFilter = isAdmin && selectedWarehouseId ? `?warehouseId=${selectedWarehouseId}` : '';

    // Only fetch if entitled and we have a valid target (manager always has one, admin needs selection)
    const shouldFetch = isOperationsEntitled && token && (!isAdmin || selectedWarehouseId);

    const { data: response, error, isLoading } = useSWR<{ ok: boolean; insights: AIInsightsData }>(
        shouldFetch ? `/api/ai/insights${warehouseFilter}` : null,
        (url: string) => fetch(url, {
            headers: { 'Authorization': `Bearer ${token}` }
        }).then(async res => {
            if (!res.ok) {
                const text = await res.text();
                throw new Error(text || 'Failed to fetch insights');
            }
            return res.json();
        }),
        { refreshInterval: 60000 }
    );

    if (!isOperationsEntitled) {
        return (
            <div className="container mx-auto px-4 py-8 space-y-6">
                <div>
                    <h1 className="text-3xl font-bold tracking-tight text-foreground">Predictive Risk Intelligence</h1>
                    <p className="text-sm text-muted-foreground mt-1">
                        Automated delay risk scoring, cascade simulations, and proactive carrier risk outreach.
                    </p>
                </div>
                <OperationsFeatureGate
                    featureName="Predictive Risk Intelligence & Automated Risk Outreach"
                    description="Statistical delay risk scoring, dock overrun cascade modeling, carrier reliability profiling, and automated risk outreach are included in the Operations ($199 USD/month) plan and active 30-day trials. Starter ($99 USD/month) includes full dock scheduling, carrier booking, gate execution, and receiving."
                    benefits={[
                        'Historical delay-risk scoring by carrier, weekday, and appointment window',
                        'Same-dock cascade simulation and economic impact estimates',
                        'Proactive and automated carrier risk-outreach emails with audit trails',
                    ]}
                    testId="ai-insights-operations-gate"
                />
            </div>
        );
    }

    const data = response?.insights;

    const filteredTopRisky = data?.atRiskThisWeek?.topRisky.filter(item => {
        // Ack check with real-time expiry
        const expiresAt = acknowledgedItems[item.id];
        if (expiresAt && expiresAt > Date.now()) return false;

        if (riskFilter === 'all') return true;
        return (item.riskLevel || '').toLowerCase() === riskFilter;
    }) || [];

    if (error) {
        return (
            <div className="p-8 text-center text-muted-foreground">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/20 mb-4">
                    <AlertTriangle className="w-6 h-6 text-red-600 dark:text-red-400" />
                </div>
                <h3 className="text-lg font-medium text-foreground mb-1">Failed to load insights</h3>
                <p className="text-sm text-muted-foreground mb-4">
                    {error.message || "We couldn't connect to the server."}
                </p>
                <Button variant="outline" onClick={() => window.location.reload()}>Retry</Button>
            </div>
        );
    }

    if (isAdmin && !selectedWarehouseId) {
        return (
            <div className="container mx-auto px-4 py-8">
                <h1 className="text-3xl font-bold tracking-tight text-foreground mb-2">AI Control Center</h1>
                <div className="bg-card border border-border rounded-xl p-12 text-center">
                    <ShieldAlert className="w-12 h-12 text-muted-foreground/50 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-foreground mb-2">Select a Warehouse</h3>
                    <p className="text-muted-foreground max-w-md mx-auto mb-6">
                        Please select a warehouse from the dropdown above to view AI insights.
                    </p>
                    <select
                        value={selectedWarehouseId}
                        onChange={(e) => setSelectedWarehouseId(e.target.value)}
                        className="px-4 py-2 bg-background border border-border rounded-md text-sm ring-offset-background focus:ring-2 focus:ring-ring min-w-[200px]"
                    >
                        <option value="">Select Warehouse...</option>
                        {user.warehouseId && <option value={user.warehouseId}>My Warehouse</option>}
                    </select>
                </div>
            </div>
        );
    }

    if (isLoading || !data) {
        return (
            <div className="flex h-96 items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
        );
    }

    return (
        <div className="container mx-auto px-4 py-8 max-w-7xl animate-in fade-in duration-500">
            {/* Header with Admin Filter */}
            <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4">
                <div>
                    <h1 className="text-3xl font-bold tracking-tight text-foreground">AI Control Center</h1>
                    <p className="text-muted-foreground mt-1">Real-time risk analysis and operational intelligence.</p>
                    <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
                        This system analyzes historical carrier performance, time-slot patterns, and recent dock outcomes to predict delay risk before it disrupts your operation.
                    </p>
                </div>

                {isAdmin && (
                    <select
                        value={selectedWarehouseId}
                        onChange={(e) => setSelectedWarehouseId(e.target.value)}
                        className="px-3 py-2 bg-background border border-border rounded-md text-sm ring-offset-background focus:ring-2 focus:ring-ring"
                    >
                        <option value="">Select Warehouse...</option>
                        {user.warehouseId && <option value={user.warehouseId}>My Warehouse</option>}
                    </select>
                )}
            </div>

            {/* AI Brief Section */}
            {data.aiBrief && (
                <div className="mb-6 bg-gradient-to-br from-slate-800 to-slate-700 rounded-xl border border-slate-600/40 p-6 relative overflow-hidden shadow-lg">
                    <div className="absolute top-0 right-0 p-3 opacity-10 pointer-events-none">
                        <Sparkles className="w-48 h-48 text-indigo-300/80" />
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 relative z-10">
                        {/* Column 1: Brief & Summary */}
                        <div className="lg:col-span-1 space-y-4">
                            <div>
                                <div className="flex items-center gap-2 mb-2">
                                    <Badge variant="neutral" className={`${
                                        data.aiBrief.confidence.label.includes('Limited') ? 'bg-orange-500/20 text-orange-300 border-orange-500/30' :
                                        data.aiBrief.confidence.label.includes('Moderate') ? 'bg-amber-500/20 text-amber-300 border-amber-500/30' :
                                        'bg-emerald-500/20 text-emerald-300 border-emerald-500/30'
                                    }`}>
                                        <Sparkles className="w-3 h-3 mr-1" />
                                        {data.aiBrief.confidence.label}
                                    </Badge>
                                    <span className="text-xs text-slate-300/80">{data.aiBrief.confidence.detail}</span>
                                </div>
                                <p className="text-slate-200 leading-relaxed font-light text-lg">
                                    {data.aiBrief.summary}
                                </p>
                            </div>

                            <ul className="space-y-2">
                                {data.aiBrief.bullets.map((bullet, idx) => (
                                    <li key={idx} className="flex items-start gap-2 text-sm text-slate-300">
                                        <div className="mt-1.5 w-1 h-1 rounded-full bg-indigo-400 shrink-0" />
                                        {bullet}
                                    </li>
                                ))}
                            </ul>
                        </div>

                        {/* Column 2: Next Best Actions */}
                        <div className="lg:col-span-1 border-t lg:border-t-0 lg:border-l border-slate-500/30 pt-6 lg:pt-0 lg:pl-8">
                            <h3 className="text-sm font-bold uppercase text-slate-300 mb-4 flex items-center gap-2">
                                <ArrowRight className="w-4 h-4" /> Next Best Actions
                            </h3>
                            <div className="space-y-3">
                                {data.nextActions?.map(action => (
                                    <div key={action.id} className="group bg-white/[0.07] hover:bg-white/[0.12] border border-white/10 hover:border-indigo-400/50 rounded-lg p-3 transition-all">
                                        <div className="flex justify-between items-start mb-1">
                                            <div className="font-medium text-slate-200">{action.label}</div>
                                            {action.count !== undefined && action.count > 0 && (
                                                <span className="bg-indigo-600 text-white text-xs px-1.5 py-0.5 rounded-full font-bold">
                                                    {action.count}
                                                </span>
                                            )}
                                        </div>
                                        <div className="text-xs text-slate-300/80 mb-3">{action.description}</div>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            className="w-full justify-between group-hover:bg-indigo-600 group-hover:border-indigo-500 group-hover:text-white"
                                            onClick={() => setLocation(action.href)}
                                        >
                                            {action.cta}
                                            <ArrowRight className="w-3 h-3 opacity-50 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />
                                        </Button>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Column 3: Anomalies */}
                        <div className="lg:col-span-1 border-t lg:border-t-0 lg:border-l border-slate-500/30 pt-6 lg:pt-0 lg:pl-8">
                            <h3 className="text-sm font-bold uppercase text-slate-300 mb-4 flex items-center gap-2">
                                <AlertTriangle className="w-4 h-4" /> Detected Anomalies
                            </h3>
                            {data.anomalies?.length === 0 ? (
                                <div className="text-slate-400 text-sm italic">No active anomalies detected.</div>
                            ) : (
                                <div className="space-y-3">
                                    {data.anomalies?.map((anomaly, idx) => (
                                        <div key={idx} className="flex gap-3 items-start p-2 rounded hover:bg-white/5">
                                            <div className={`mt-1.5 w-2 h-2 rounded-full shrink-0 ${anomaly.severity === 'high' ? 'bg-red-500 shadow-[0_0_8px_rgba(239,68,68,0.5)]' :
                                                anomaly.severity === 'medium' ? 'bg-orange-500' : 'bg-yellow-500'
                                                }`} />
                                            <div>
                                                <div className="text-sm font-medium text-slate-200">{anomaly.title}</div>
                                                <div className="text-xs text-slate-300/70 mt-0.5">{anomaly.detail}</div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* WOW Metric & Top Level Stats */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                {/* 1. WOW Headline Metric */}
                <KPICard
                    icon={DollarSign}
                    label={data.headlineMetric.label.toUpperCase()}
                    value={`$${data.headlineMetric.value.toLocaleString()}`}
                    trend={{ value: 'Conservative Estimate', direction: 'neutral' }}
                    className="border-primary/20 bg-primary/5 dark:bg-primary/10"
                />

                {/* 2. Outreach Stats */}
                <div className="bg-card border border-border rounded-xl p-5 flex flex-col justify-between">
                    <div>
                        <div className="flex items-center gap-2 mb-2">
                            <Mail className="w-4 h-4 text-blue-500" />
                            <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Risk Outreach</span>
                        </div>
                        <div className="flex items-baseline gap-2">
                            <span className="text-3xl font-bold">{data.outreach.recommended}</span>
                            <span className="text-sm text-muted-foreground">recommended</span>
                        </div>
                    </div>
                    <div className="mt-4 pt-4 border-t border-dashed border-border flex justify-between text-sm">
                        <div className="flex flex-col">
                            <span className="text-muted-foreground text-xs">Sent (30d)</span>
                            <span className="font-mono font-medium">{data.outreach.sentLast30d}</span>
                        </div>
                    </div>
                </div>

                {/* 3. Outcome Action */}
                <div className="bg-card border border-border rounded-xl p-5 flex flex-col justify-between">
                    <div>
                        <div className="flex items-center gap-2 mb-2">
                            {data.outcomes.missingCount > 5 ? (
                                <AlertTriangle className="w-4 h-4 text-amber-500" />
                            ) : (
                                <CheckCircle className="w-4 h-4 text-green-500" />
                            )}
                            <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Data Completeness</span>
                        </div>
                        <div className="flex items-baseline gap-2">
                            <span className="text-3xl font-bold">{data.outcomes.missingCount}</span>
                            <span className="text-sm text-muted-foreground">incomplete outcomes</span>
                        </div>
                    </div>
                    <div className="mt-4">
                        <Button
                            variant="outline"
                            size="sm"
                            className="w-full justify-between"
                            onClick={() => setLocation('/appointments?status=completed&outcome=missing')} // Wouter navigation
                        >
                            Review Missing Outcomes
                            <ArrowRight className="w-3 h-3 ml-2" />
                        </Button>
                    </div>
                </div>

                {/* 4. Total At-Risk Count (Summary) */}
                <KPICard
                    icon={AlertTriangle}
                    label="High Risk Arrivals (7d)"
                    value={data.atRiskThisWeek.high}
                    trend={data.atRiskThisWeek.high > 0 ? { value: 'Requires Attention', direction: 'down' } : undefined}
                />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* LEFT COL: At-Risk Panel (2 cols wide) */}
                <div className="lg:col-span-2 space-y-6">
                    <div className="bg-card border border-border rounded-xl overflow-hidden shadow-sm">
                        <div className="p-6 border-b border-border flex items-center justify-between bg-slate-50 dark:bg-transparent">
                            <div className="flex items-center gap-2">
                                <ShieldAlert className="w-5 h-5 text-orange-500" />
                                <h2 className="text-lg font-semibold">At-Risk Appointments (Next 7 Days)</h2>
                            </div>
                            <div className="flex gap-2">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setIsQueueOpen(true)}
                                    disabled={filteredTopRisky.length === 0}
                                    className="h-7 text-xs mr-2"
                                >
                                    <ListChecks className="w-3.5 h-3.5 mr-1.5" />
                                    Review Queue
                                </Button>
                                <button
                                    type="button"
                                    aria-pressed={riskFilter === 'all'}
                                    onClick={() => setRiskFilter('all')}
                                    className={`rounded-md transition-all focus:outline-none focus:ring-2 focus:ring-slate-400 focus:ring-offset-1 ${riskFilter === 'all' ? 'ring-2 ring-slate-400 dark:ring-slate-600' : 'hover:opacity-80 opacity-70 hover:opacity-100'}`}
                                >
                                    <Badge
                                        className={`${riskFilter === 'all' ? 'bg-slate-200 dark:bg-slate-800 text-slate-900 dark:text-slate-100' : 'bg-slate-100 dark:bg-slate-900 text-slate-500 border-slate-200 dark:border-slate-800 border'}`}
                                    >
                                        All
                                    </Badge>
                                </button>

                                <button
                                    type="button"
                                    aria-pressed={riskFilter === 'high'}
                                    onClick={() => setRiskFilter(riskFilter === 'high' ? 'all' : 'high')}
                                    className={`rounded-md transition-all focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-1 ${riskFilter === 'high' ? 'ring-2 ring-red-500/50' : 'hover:opacity-80'}`}
                                >
                                    <Badge
                                        className={`${riskFilter === 'high' ? 'bg-red-500/20' : 'bg-red-500/10'} text-red-600 border-red-200 dark:border-red-900 border`}
                                    >
                                        High: {data.atRiskThisWeek.high}
                                    </Badge>
                                </button>

                                <button
                                    type="button"
                                    aria-pressed={riskFilter === 'medium'}
                                    onClick={() => setRiskFilter(riskFilter === 'medium' ? 'all' : 'medium')}
                                    className={`rounded-md transition-all focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-1 ${riskFilter === 'medium' ? 'ring-2 ring-orange-500/50' : 'hover:opacity-80'}`}
                                >
                                    <Badge
                                        className={`${riskFilter === 'medium' ? 'bg-orange-500/20' : 'bg-orange-500/10'} text-orange-600 border-orange-200 dark:border-orange-900 border`}
                                    >
                                        Med: {data.atRiskThisWeek.medium}
                                    </Badge>
                                </button>

                                <button
                                    type="button"
                                    aria-pressed={riskFilter === 'low'}
                                    onClick={() => setRiskFilter(riskFilter === 'low' ? 'all' : 'low')}
                                    className={`rounded-md transition-all focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-1 ${riskFilter === 'low' ? 'ring-2 ring-green-500/50' : 'hover:opacity-80'}`}
                                >
                                    <Badge
                                        className={`${riskFilter === 'low' ? 'bg-green-500/20' : 'bg-green-500/10'} text-green-600 border-green-200 dark:border-green-900 border`}
                                    >
                                        Low: {data.atRiskThisWeek.low}
                                    </Badge>
                                </button>
                            </div>
                        </div>

                        {filteredTopRisky.length === 0 ? (
                            <div className="p-12 text-center text-muted-foreground bg-muted/20">
                                <CheckCircle className="w-12 h-12 mx-auto mb-3 text-slate-300 dark:text-slate-700" />
                                <p>No {riskFilter !== 'all' ? riskFilter + '-risk' : 'risky'} appointments found.</p>
                            </div>
                        ) : (
                            <div className="divide-y divide-border">
                                {filteredTopRisky.map((appt) => (
                                    <div
                                        key={appt.id}
                                        className="p-4 hover:bg-muted/30 transition-colors cursor-pointer flex items-start gap-4"
                                        onClick={() => openAppointmentDetail({
                                            id: appt.id,
                                            po_number: appt.poNumber || 'N/A',
                                            carrier_name: appt.carrierName,
                                            status: appt.status || 'approved', // Use real status
                                            scheduled_time: appt.startAt
                                        })}
                                    >
                                        <div className="shrink-0 pt-1">
                                            {appt.riskLevel?.toLowerCase() === 'high' ? (
                                                <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                                            ) : (
                                                <div className="w-2 h-2 rounded-full bg-orange-500" />
                                            )}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center justify-between gap-2 mb-1">
                                                <h3 className="font-medium text-foreground truncate">
                                                    {appt.carrierName}
                                                </h3>
                                                <div className="flex items-center gap-2">
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            openAppointmentDetail({
                                                                id: appt.id,
                                                                po_number: appt.poNumber || 'N/A',
                                                                carrier_name: appt.carrierName,
                                                                status: appt.status || 'approved',
                                                                scheduled_time: appt.startAt
                                                            }, { scrollToRisk: true });
                                                        }}
                                                        className="text-[10px] uppercase font-bold text-indigo-600 dark:text-indigo-400 hover:text-indigo-500 dark:hover:text-indigo-300 hover:underline px-2 py-0.5"
                                                    >
                                                        Why?
                                                    </button>
                                                    <span className="text-xs text-muted-foreground whitespace-nowrap">
                                                        {new Date(appt.startAt).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                                                    </span>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                                                <span className="font-mono text-xs bg-muted px-1.5 py-0.5 rounded">
                                                    {appt.poNumber || 'No PO'}
                                                </span>
                                                <span>•</span>
                                                <span>Risk Score: {Math.round(appt.riskScore * 100)}%</span>
                                            </div>
                                            {appt.reasons && appt.reasons.length > 0 && (
                                                <div className="flex flex-wrap gap-1.5">
                                                    {appt.reasons.map((reason, idx) => (
                                                        <span key={idx} className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-300 border border-red-100 dark:border-red-900/50">
                                                            {reason}
                                                        </span>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                        <div className="shrink-0 self-center">
                                            <Button variant="ghost" size="icon" className="h-8 w-8">
                                                <ArrowRight className="w-4 h-4 text-muted-foreground" />
                                            </Button>
                                        </div>
                                    </div>
                                ))}
                                <div className="p-3 text-center bg-muted/10">
                                    <Button variant="link" size="sm" className="text-xs text-muted-foreground" onClick={() => setLocation('/appointments')}>
                                        View All Upcoming
                                    </Button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* RIGHT COL: Leaderboard (1 col wide) */}
                <div className="space-y-6">
                    <div className="bg-card border border-border rounded-xl overflow-hidden shadow-sm h-full">
                        <div className="p-6 border-b border-border flex items-center gap-2 bg-slate-50 dark:bg-transparent">
                            <TrendingUp className="w-5 h-5 text-purple-500" />
                            <h2 className="text-lg font-semibold">Risk Leaderboard</h2>
                        </div>
                        <div className="p-0">
                            <div className="px-6 py-3 bg-slate-50 dark:bg-muted/20 text-xs font-semibold uppercase tracking-wider text-muted-foreground flex justify-between">
                                <span>Carrier (30d)</span>
                                <span>Late Rate</span>
                            </div>
                            {data.carrierLeaderboard.length === 0 ? (
                                <div className="p-8 text-center text-sm text-muted-foreground">
                                    Not enough data to rank carriers yet.
                                </div>
                            ) : (
                                <div className="divide-y divide-border/50">
                                    {data.carrierLeaderboard.map((carrier, idx) => (
                                        <div key={idx} className="px-6 py-4 flex items-center justify-between hover:bg-muted/10 transition-colors">
                                            <div className="flex items-center gap-3 min-w-0 flex-1">
                                                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${idx === 0 ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400' :
                                                    idx === 1 ? 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300' :
                                                        idx === 2 ? 'bg-orange-50 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400' :
                                                            'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400'
                                                    }`}>
                                                    {idx + 1}
                                                </div>
                                                <div className="truncate">
                                                    <div className="text-sm font-medium text-foreground truncate" title={carrier.name}>
                                                        {carrier.name}
                                                    </div>
                                                    <div className="text-[10px] text-muted-foreground">
                                                        {carrier.total} loads • {carrier.lateCount} late
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="font-mono text-sm font-semibold text-red-600 dark:text-red-400">
                                                {Math.round(carrier.lateRate * 100)}%
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                        <div className="p-4 bg-muted/5 text-center border-t border-border">
                            <p className="text-[10px] text-muted-foreground">
                                <Info className="w-3 h-3 inline mr-1" />
                                Based on completed appointments over the last 30 days
                            </p>
                        </div>
                    </div>
                </div>
            </div>
            <AppointmentDetailDialog />

            <ReviewQueueDrawer
                isOpen={isQueueOpen}
                onClose={() => setIsQueueOpen(false)}
                items={filteredTopRisky}
                onAcknowledge={handleAcknowledge}
                token={token || ''}
            />
        </div>
    );
}
